// ============================================================
//  DynamicTabHeader — Enhanced Tab Navigation with Management
// ============================================================

import React, { useState } from 'react';
import { HeaderState } from '../../../types/index';
import { useTabContext } from '../providers/TabProvider';
import { TabManager } from './TabManager';
import './DynamicTabHeader.css';

interface Props {
  header: HeaderState;
  onSwitchPage: (tabId: string) => void;
}

export function DynamicTabHeader({
  header,
  onSwitchPage,
}: Props): React.ReactElement {
  const { tabs, activeTab, setActiveTab } = useTabContext();

  const [showManager, setShowManager] = useState(false);
  const [isScrollable, setIsScrollable] = useState(false);
  const [previousActiveTab, setPreviousActiveTab] = useState<string | null>(
    activeTab
  );

  // Handle when activeTab changes (especially when new tabs are added)
  React.useEffect(() => {
    if (activeTab && activeTab !== previousActiveTab) {
      onSwitchPage(activeTab);
      setPreviousActiveTab(activeTab);
    }
  }, [activeTab, previousActiveTab, onSwitchPage]);

  const handleTabSwitch = (tabId: string): void => {
    setActiveTab(tabId);
    onSwitchPage(tabId);
  };

  const handleTabManagement = (tabId: string): void => {
    setActiveTab(tabId);
    onSwitchPage(tabId);
    setShowManager(false);
  };

  // Check if tabs are scrollable
  React.useEffect(() => {
    const container = document.querySelector('.dynamic-tabs-container');
    if (container) {
      setIsScrollable(container.scrollWidth > container.clientWidth);
    }
  }, [tabs]);

  return (
    <>
      <div className="dynamic-tab-header">
        <div
          className={`dynamic-tabs-container ${
            isScrollable ? 'scrollable' : ''
          }`}
          onScroll={(e) => {
            const container = e.currentTarget;
            setIsScrollable(container.scrollWidth > container.clientWidth);
          }}
        >
          <div className="dynamic-tabs">
            {tabs
              .sort((a: any, b: any) => a.order - b.order)
              .map((tab: any) => (
                <button
                  key={tab.id}
                  className={`dynamic-tab ${
                    tab.id === activeTab ? 'active' : ''
                  } ${tab.isDefault ? 'default' : 'custom'}`}
                  onClick={() => handleTabSwitch(tab.id)}
                  title={`${tab.label} (/${tab.slug})`}
                >
                  <span className="tab-label">{tab.label}</span>
                </button>
              ))}
          </div>
        </div>

        <div className="tab-controls">
          {isScrollable && (
            <div className="scroll-indicators">
              <button
                className="scroll-btn scroll-left"
                onClick={() => {
                  const container = document.querySelector(
                    '.dynamic-tabs-container'
                  );
                  if (container) {
                    container.scrollBy({ left: -120, behavior: 'smooth' });
                  }
                }}
                title="Défiler à gauche"
              >
                ‹
              </button>
              <button
                className="scroll-btn scroll-right"
                onClick={() => {
                  const container = document.querySelector(
                    '.dynamic-tabs-container'
                  );
                  if (container) {
                    container.scrollBy({ left: 120, behavior: 'smooth' });
                  }
                }}
                title="Défiler à droite"
              >
                ›
              </button>
            </div>
          )}

          <button
            className="manage-tabs-btn"
            onClick={() => setShowManager(true)}
            title="Gérer les pages"
          >
            <span className="manage-icon">⚙️</span>
            <span className="manage-label">Pages</span>
          </button>
        </div>
      </div>

      {/* Tab Manager Modal */}
      {showManager && (
        <div className="tab-manager-overlay">
          <TabManager
            onTabChange={handleTabManagement}
            onClose={() => setShowManager(false)}
          />
        </div>
      )}
    </>
  );
}

// New enhanced site header that includes both logo and dynamic tabs
interface SiteHeaderProps {
  header: HeaderState;
  isSelected: boolean;
  onSelect: () => void;
  onSwitchPage: (tabId: string) => void;
}

export function EnhancedSiteHeader({
  header: h,
  isSelected,
  onSelect,
  onSwitchPage,
}: SiteHeaderProps): React.ReactElement {
  return (
    <>
      {/* Logo Header */}
      <div
        onClick={onSelect}
        title="Cliquer pour modifier le header"
        className="site-header-top"
        style={{
          background: h.bgImageSrc
            ? `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url(${h.bgImageSrc}) center/cover`
            : h.bgColor,
          boxShadow: h.shadow ? '0 1px 4px rgba(0,0,0,0.08)' : undefined,
          cursor: 'pointer',
          outline: isSelected
            ? '2px solid var(--accent)'
            : '2px solid transparent',
          outlineOffset: -2,
          transition: 'outline 0.1s',
          position: 'relative',
        }}
      >
        {/* Edit hint */}
        <div
          style={{
            position: 'absolute',
            top: 4,
            right: 8,
            zIndex: 10,
            pointerEvents: 'none',
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '0.06em',
            color: 'var(--accent)',
            background: 'var(--accent-dim)',
            padding: '2px 8px',
            borderRadius: 4,
            opacity: isSelected ? 1 : 0,
            transition: 'opacity 0.2s',
          }}
        >
          Modifier le header
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '0 32px',
            gap: 20,
            height: `${h.height}px`,
          }}
        >
          {/* Logo */}
          {h.logoType === 'image' && h.logoImageSrc ? (
            <img
              src={h.logoImageSrc}
              alt={h.logoText}
              style={{
                height: Math.round(parseInt(h.height) * 0.55),
                width: 'auto',
                verticalAlign: 'middle',
              }}
            />
          ) : (
            <span
              style={{
                fontWeight: 800,
                fontSize: 18,
                letterSpacing: '-0.02em',
                color: h.textColor,
              }}
            >
              {h.logoText}
            </span>
          )}

          <span style={{ flex: 1 }} />
        </div>
      </div>

      {/* Dynamic Tabs */}
      <DynamicTabHeader header={h} onSwitchPage={onSwitchPage} />
    </>
  );
}
